import { inheritRequestCorrelation } from '@brighthub/structured-logging';
import { Injectable, inject } from '@angular/core';
import { ApiService, GenericResponseCode } from '@brighthub/api';
import { Observable, defer, firstValueFrom, map } from 'rxjs';
import {
  CarePlanOption,
  CreateOptionValue,
  UpdateOptionValue,
} from '../options.model';
import {
  CreateOptionRequest,
  OptionApiEnvelope,
  OptionListPayload,
  OptionResource,
  UpdateOptionRequest,
} from './option-api.model';
import { OptionApi } from './option-api.token';

const API_NAME = 'api-internal';
const OPTION_PAGE_LIMIT = 100;

/** Live authenticated client for AIM's case-owned Option v3 endpoints. */
@Injectable()
export class HttpOptionApiService implements OptionApi {
  private readonly _api = inject(ApiService);

  listOptions(caseId: string): Observable<CarePlanOption[]> {
    const numericCaseId = this._id(caseId, 'case');
    return defer(() => this._loadAllOptions(numericCaseId));
  }

  createOption(
    caseId: string,
    value: CreateOptionValue,
  ): Observable<CarePlanOption> {
    const numericCaseId = this._id(caseId, 'case');
    const body: CreateOptionRequest = {
      name: value.title.trim(),
      optionTypeCode: value.optionTypeCode.trim().toUpperCase(),
      ...(value.description?.trim()
        ? { description: value.description.trim() }
        : {}),
      ...(value.statusCode
        ? { statusCode: value.statusCode.trim().toUpperCase() }
        : {}),
    };
    return this._api
      .post<OptionApiEnvelope<OptionResource>>(
        API_NAME,
        `/case/${numericCaseId}/option`,
        body,
        {
          suppressRequestBodyTelemetry: true,
          telemetryMetadata: {
            caseId: numericCaseId,
            optionTypeCode: body.optionTypeCode,
            ...(body.statusCode ? { statusCode: body.statusCode } : {}),
          },
        },
      )
      .pipe(
        map((response) => this._payload(response)),
        map((resource) => this._option(resource)),
      );
  }

  updateOption(
    caseId: string,
    optionId: string,
    value: UpdateOptionValue,
  ): Observable<CarePlanOption> {
    const numericCaseId = this._id(caseId, 'case');
    const numericOptionId = this._id(optionId, 'option');
    const body: UpdateOptionRequest = {
      ...(value.title !== undefined ? { name: value.title.trim() } : {}),
      ...(value.description !== undefined
        ? { description: value.description.trim() }
        : {}),
      ...(value.statusCode !== undefined
        ? { statusCode: value.statusCode.trim().toUpperCase() }
        : {}),
    };
    return this._api
      .patch<OptionApiEnvelope<OptionResource>>(
        API_NAME,
        `/case/${numericCaseId}/option/${numericOptionId}`,
        body,
        {
          suppressRequestBodyTelemetry: true,
          telemetryMetadata: {
            caseId: numericCaseId,
            optionId: numericOptionId,
            operation: 'update',
            ...(body.statusCode ? { statusCode: body.statusCode } : {}),
          },
        },
      )
      .pipe(
        map((response) => this._payload(response)),
        map((resource) => this._option(resource)),
      );
  }

  private async _loadAllOptions(caseId: number): Promise<CarePlanOption[]> {
    const options = new Map<string, CarePlanOption>();
    const visitedOffsets = new Set<number>();
    let offset = 0;

    while (true) {
      if (visitedOffsets.has(offset)) {
        throw new Error('Option API pagination did not advance.');
      }
      visitedOffsets.add(offset);

      const response = await firstValueFrom(
        this._api.get<OptionApiEnvelope<OptionListPayload | OptionResource[]>>(
          API_NAME,
          `/case/${caseId}/option?limit=${OPTION_PAGE_LIMIT}&offset=${offset}`,
        ),
      );
      const payload = this._payload(response);
      const resources = this._resources(payload);
      for (const resource of resources) {
        try {
          const option = this._option(resource);
          options.set(option.id, option);
        } catch {
          // A malformed row must not prevent the remaining valid Options from rendering.
        }
      }

      if (Array.isArray(payload) || !payload.resultSet) {
        return [...options.values()];
      }
      if (payload.resultSet.allFetched) return [...options.values()];

      const reportedOffset = payload.resultSet.offset;
      const fallbackOffset = offset + resources.length;
      const nextOffset =
        reportedOffset > offset ? reportedOffset : fallbackOffset;
      if (nextOffset <= offset) {
        throw new Error('Option API pagination did not advance.');
      }
      offset = nextOffset;
    }
  }

  private _id(value: string | number, kind: 'case' | 'option'): number {
    const id = Number(value);
    if (!Number.isSafeInteger(id) || id <= 0) {
      throw new Error(`A valid ${kind} id is required.`);
    }
    return id;
  }

  private _resources(
    payload: OptionListPayload | OptionResource[],
  ): OptionResource[] {
    if (Array.isArray(payload)) return payload;
    return payload.options ?? payload.items ?? [];
  }

  private _option(resource: OptionResource): CarePlanOption {
    const optionId = this._id(resource.optionId, 'option');
    const caseId = this._id(resource.caseId, 'case');
    const title = resource.name?.trim();
    const optionTypeCode = resource.optionTypeCode?.trim().toUpperCase();
    const statusCode = resource.statusCode?.trim().toUpperCase();
    if (!title || !optionTypeCode || !statusCode) {
      throw new Error('Option API returned an invalid option.');
    }
    return {
      id: String(optionId),
      caseId: String(caseId),
      title,
      description: resource.description?.trim() || null,
      optionTypeCode,
      statusCode,
      sourceTypeCode: resource.sourceTypeCode?.trim().toUpperCase() || null,
      sourceId: resource.sourceId ?? null,
      createdAt: resource.createdAt,
      updatedAt: resource.updatedAt,
    };
  }

  private _payload<T>(response: OptionApiEnvelope<T>): T {
    if (
      response?.result?.code !== GenericResponseCode.SERVICE_OK ||
      response.payload == null
    ) {
      throw inheritRequestCorrelation(
        Object.assign(
          new Error(
            response?.result?.message ||
              'Option API returned an invalid response',
          ),
          { status: response?.result?.status },
        ),
        response,
      );
    }
    return response.payload;
  }
}
